import { useState, useEffect } from "react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import FormGroup from "react-bootstrap/FormGroup";
import GooglePlacesAutocomplete, {
  geocodeByPlaceId,
} from "react-google-places-autocomplete";
import { isPossiblePhoneNumber } from "libphonenumber-js";
import { Form } from "react-bootstrap";

import FormBlock from "../../FormBlock";
import { useAppContext } from "../../../../_context";
import SignupField from "../../SignupField";
import Button from "../../../Misc/Button";
import TelephoneInput from "../../../TelephoneInput";
import FieldError from "../../../FieldError";
import { apiUrl, getId, query } from "../../../../utilities/Helpers";
import UploadMultipleFileTarget from "../../../Upload/UploadMultipleFileTarget";
import DayRows from "./DayRows";
import CourtSection from "./CourtSection";
import {
  validateEmail,
  idRegex,
  clubCreatedByUsLabel,
} from "../../../../utilities/validations";
import ReCaptcha from "../../../ReCaptcha";

const FormSection = ({ setMessageSent }) => {
  const [{ tx }] = useAppContext();
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showRepresentativeOther, setShowRepresentativeOther] = useState(false);
  const [placesPostCode, setPlacesPostcode] = useState({});
  const [recaptchaVerified, setRecaptchaVerified] = useState(false);
  const [clubId, setClubId] = useState("");
  const [otherFieldValue, setOtherFieldValue] = useState("");

  useEffect(() => {
    setClubId(getId());

    return () => setClubId("");
  }, []);

  useEffect(() => {
    const id =
      otherFieldValue.toLowerCase().includes(clubCreatedByUsLabel) &&
      otherFieldValue.toLowerCase().replace(clubCreatedByUsLabel, "").trim();

    if (idRegex.test(id)) {
      setClubId(id);
    }
  }, [otherFieldValue]);

  const showOtherIfNeeded = (e) => {
    setShowRepresentativeOther(e?.target?.value === "other");
  };

  const submitToApi = async (payload) => {
    setSubmitting(true);
    return await fetch(apiUrl("/api/v1/web/createClub"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-version": "New",
      },
      body: JSON.stringify(payload),
    });
  };

  const validate = (payload) => {
    const newErrors = {};
    let firstError = null;

    if (!payload.user.name) {
      newErrors["signup-user-name"] = null;
      firstError = "signup-user-name";
    }

    if (!validateEmail(payload.user.email)) {
      newErrors["signup-user-email"] = tx["Please enter a valid email address"];
      firstError = firstError || "signup-user-email";
    }

    if (!payload.user.phone) {
      newErrors["signup-user-phone-number"] = null;
      firstError = firstError || "signup-user-phone-number";
    }

    try {
      if (!isPossiblePhoneNumber(payload.user.phone)) {
        newErrors["signup-user-phone-number"] =
          tx["Please enter a valid phone number"];
        firstError = firstError || "signup-user-phone-number";
      }
    } catch (e) {
      if (!isPossiblePhoneNumber(payload.phone)) {
        newErrors["signup-user-phone-number"] =
          tx["Please enter a valid phone number"];
        firstError = firstError || "signup-phone-number";
      }
    }

    if (!payload.images || payload.images.length < 1) {
      newErrors["signup-avatar-urls"] =
        tx["Please add at least one photo of your club"];
      firstError = firstError || "signup-avatar-urls";
    }

    if (!payload.user.type) {
      newErrors["signup-user-type"] = null;
      firstError = firstError || "signup-user-type";
    }

    if (showRepresentativeOther && !payload.user.typeOther) {
      newErrors["signup-user-type-other"] = null;
      firstError = firstError || "signup-user-type-other";
    }

    if (!payload.name) {
      newErrors["signup-club-name"] = null;
      firstError = firstError || "signup-club-name";
    }

    if (!payload.location) {
      newErrors["signup-location"] = null;
      firstError = firstError || "signup-location";
    }

    if (payload.contact.email && !payload.contact.email.match(/.+@.+\..+/)) {
      newErrors["signup-club-email"] = tx["Please enter a valid email address"];
      firstError = firstError || "signup-club-email";
    }

    if (!payload.type) {
      newErrors["signup-club-type"] = null;
      firstError = firstError || "signup-club-type";
    }

    (payload.openingHours || []).forEach((item) => {
      if (item.is_open) {
        if (!item.open) {
          newErrors[`day-row-${item.day}-open-time`] = null;
          firstError = firstError || `day-row-${item.day}-open-time`;
        }
        if (!item.close) {
          newErrors[`day-row-${item.day}-close-time`] = null;
          firstError = firstError || `day-row-${item.day}-close-time`;
        }
      }
    });

    if (!payload.about) {
      newErrors["signup-about"] = null;
      firstError = firstError || "signup-about";
    }

    (payload.courts || []).forEach((item) => {
      if (!item.sport) {
        const elemId = `signup-court-type-${item.index}`;
        newErrors[elemId] = null;
        firstError = firstError || elemId;
      }

      if (item.surface?.includes("other") && !item.surfaceOther) {
        const elemId = `signup-surface-other-${item.index}`;
        newErrors[elemId] = null;
        firstError = firstError || elemId;
      }
    });

    if (!recaptchaVerified) {
      newErrors["clubs-recaptcha"] =
        tx["Please complete the captcha to continue"];
      firstError = firstError || "clubs-recaptcha";
    }

    if (firstError) {
      const errorElem = query(`#${firstError}`);

      if (errorElem?.type?.toLowerCase() === "hidden") {
        try {
          errorElem.parentNode.scrollIntoView({ block: "center" });
        } catch (e) {
          return;
        }
      } else {
        errorElem?.focus();
      }

      setErrors(newErrors);
      return false;
    }

    return true;
  };

  const handleServerValidationErrors = (errors = [{}]) => {
    const map = [
      { index: 0, key: "user.name", selector: "signup-user-name" },
      { index: 1, key: "user.email", selector: "signup-user-email" },
      { index: 2, key: "user.phone", selector: "signup-user-phone-number" },
      { index: 3, key: "user.type", selector: "signup-user-type" },
      { index: 4, key: "images", selector: "signup-avatar-urls" },
      { index: 5, key: "name", selector: "signup-club-name" },
      { index: 6, key: "location", selector: "signup-location" },
      { index: 7, key: "contact.email", selector: "signup-club-email" },
      { index: 8, key: "contact.phone", selector: "signup-club-phone-number" },
      { index: 9, key: "contact.website", selector: "signup-website" },
      { index: 10, key: "type", selector: "signup-club-type" },
      { index: 11, key: "about", selector: "signup-about" },
    ];

    let firstIndex = null;
    let firstError = null;
    const output = {};

    errors.forEach((e) => {
      if (!e.field) {
        output["generic-error"] = tx["be_unexpected_error"];
      } else {
        const serverId = e.field.join(".");
        const mapItem = map.find((item) => item.key === serverId);
        const htmlId = mapItem?.selector;

        if (htmlId) {
          if (firstIndex === null || firstIndex > mapItem.index) {
            firstIndex = mapItem.index;
            firstError = htmlId;
          }

          output[htmlId] = e.error || null;
        } else {
          output["generic-error"] = tx["Please check the form and try again"];
        }
      }
    });

    firstError && query(`#${firstError}`)?.focus();
    setErrors(output);
  };

  const handleSecondaryServerValidationErrors = (errors) => {
    const output = [];

    for (let x in errors) {
      output.push({
        field: [x],
        error: errors[x].message,
      });
    }

    handleServerValidationErrors(output);
  };

  const getOpeningHours = () => {
    const output = [];

    query(".day-row-item", true).forEach((container) => {
      const index = container.dataset["index"];
      const isOpen = query(".is-open", false, container).checked;
      let time = "closed";
      let open = null;
      let close = null;

      if (isOpen) {
        open = query(".day-row-open", false, container).value.trim();
        close = query(".day-row-close", false, container).value.trim();
        time = `${open}-${close}`;
      }

      output.push({ day: index, time, open, close, is_open: isOpen });
    });

    return output;
  };

  const getLocation = async () => {
    if (!placesPostCode?.value?.place_id) {
      return false;
    }

    const addresses = await geocodeByPlaceId(placesPostCode.value.place_id);

    const getCountry = (location) => {
      const country = location.address_components.filter(
        (component) => component.types.indexOf("country") !== -1,
      );
      return country.length > 0 ? country[0].short_name : null;
    };

    const getCity = (location) => {
      let city = location.address_components.filter(
        (component) =>
          component.types.indexOf("administrative_area_level_2") !== -1,
      );
      if (city.length > 0) return city[0].long_name;

      city = location.address_components.filter(
        (component) =>
          component.types.indexOf("administrative_area_level_1") !== -1,
      );

      if (city.length > 0) return city[0].long_name;

      const countryAsCity = getCountry(location);
      if (countryAsCity) {
        return countryAsCity.long_name;
      }
      return "";
    };

    return {
      address: addresses[0].formatted_address,
      coordinates: [
        addresses[0].geometry.location.lng(),
        addresses[0].geometry.location.lat(),
      ],
      city: getCity(addresses[0]),
      country: getCountry(addresses[0]),
      googlePlaceId: addresses[0].place_id,
    };
  };

  const submit = async () => {
    if (submitting) {
      return false;
    }
    setIsLoading(true);

    const payload = {
      _id: clubId,
      location: await getLocation(),
      user: {
        name: query("#signup-user-name").value.trim(),
        email: query("#signup-user-email").value.trim(),
        phone: query("#signup-user-phone-number").value.trim(),
        type: query("#signup-user-type").value.trim(),
        typeOther: query("#signup-user-type-other")?.value.trim() || null,
      },
      name: query("#signup-club-name").value.trim(),
      about: query("#signup-about").value.trim(),
      images: JSON.parse(query("#signup-avatar-urls").value),
      largeImageUrls: JSON.parse(query("#signup-large-image-urls").value),
      type: query("#signup-club-type").value.trim(),
      courts: JSON.parse(query("#signup-courts").value),
      contact: {
        phone: query("#signup-club-phone-number").value.trim(),
        email: query("#signup-club-email").value.trim(),
        website: query("#signup-website").value.trim(),
      },
      facilities: {
        showers: query("#facilities-showers").checked,
        changingRooms: query("#facilities-changing-rooms").checked,
        payAsYouPlay: query("#facilities-pay-as-you-play").checked,
        hospitality: query("#facilities-hospitality").checked ? "Yes" : "No",
        equipmentRental: query("#facilities-equipment-rental").checked,
        carParking: query("#facilities-parking-lot").checked,
        other: query("#facilities-others").value.trim(),
      },
      openingHours: getOpeningHours(),
    };

    if (!validate(payload)) {
      setIsLoading(false);
      return;
    }

    if (payload.user.typeOther) {
      payload.user.type = payload.user.typeOther;
      delete payload.user.typeOther;
    }

    const response = await submitToApi(payload);
    const result = await response.json();

    setIsLoading(false);

    if (!response.ok) {
      setSubmitting(false);
      if (!result.message) {
        return handleServerValidationErrors();
      }

      if (result.message === "validation error") {
        handleServerValidationErrors(result.errors);
        return;
      }

      if (result.message.match("validation failed")) {
        if (result.message.match("phone")) {
          handleSecondaryServerValidationErrors({
            phone: { message: tx["Phone number already in use"] },
          });
          return;
        }
        handleSecondaryServerValidationErrors(result.errors);
      }

      return handleServerValidationErrors();
    }

    setMessageSent(true);
    document.body.scrollTop = document.documentElement.scrollTop = 0;
  };

  const selectStyles = {
    dropdownIndicator: () => ({ display: "none" }),
    indicatorSeparator: () => ({ display: "none" }),
  };

  return (
    <section className="form-section mb-7">
      <Container>
        <Row>
          <Col md={10} lg={8} className="px-0 px-sm-4 mx-auto">
            <FormBlock label={tx["Your details"]}>
              <Col xs={12}>
                <SignupField
                  id="user-name"
                  label={tx["Full name"]}
                  mandatory={true}
                />
                <FieldError fieldName="signup-user-name" allErrors={errors} />
              </Col>

              <Col md={6}>
                <SignupField
                  id="user-email"
                  label={tx["Email"]}
                  mandatory={true}
                  type="email"
                />
                <FieldError fieldName="signup-user-email" allErrors={errors} />
              </Col>

              <Col md={6}>
                <FormGroup className="mb-3">
                  <label
                    className="form-label"
                    htmlFor="signup-user-phone-number">
                    * {tx["Phone number"]}
                  </label>
                  <TelephoneInput id="signup-user-phone-number" />
                  <FieldError
                    fieldName="signup-user-phone-number"
                    allErrors={errors}
                  />
                </FormGroup>
              </Col>

              <Col xs={12}>
                <SignupField
                  id="user-type"
                  label={`${tx["Which best describes you"]}:`}
                  mandatory={true}
                  type="select"
                  options={[
                    {
                      value: "managerOrRepresentative",
                      label: tx["Club Manager / Club Representative"],
                    },
                    { value: "player", label: tx["Player"] },
                    { value: "other", label: tx["Other (enter below)"] },
                  ]}
                  onChange={showOtherIfNeeded}
                />
                <FieldError fieldName="signup-user-type" allErrors={errors} />
              </Col>

              {showRepresentativeOther ? (
                <Col xs={12}>
                  <SignupField
                    id="user-type-other"
                    label={tx["clubs:Other"]}
                    mandatory={true}
                    type="text"
                    onChange={(e) => setOtherFieldValue(e.target.value)}
                  />
                  <FieldError
                    fieldName="signup-user-type-other"
                    allErrors={errors}
                  />
                </Col>
              ) : null}
            </FormBlock>
            <FormBlock label={tx["About the club"]}>
              <Col xs={12} className="text-center">
                <div className="upload-container">
                  <UploadMultipleFileTarget photoPrefix={clubId} />
                  <FieldError
                    fieldName="signup-avatar-urls"
                    allErrors={errors}
                    className="pb-3 text-center"
                  />
                </div>
              </Col>

              <Col md={6}>
                <SignupField
                  id="club-name"
                  label={tx["Club name"]}
                  mandatory={true}
                />
                <FieldError fieldName="signup-club-name" allErrors={errors} />
              </Col>

              <Col md={6}>
                <FormGroup className="mb-3 places-autocomplete-container">
                  <label className="form-label" htmlFor="signup-location">
                    * {tx["Location"]}
                  </label>
                  <GooglePlacesAutocomplete
                    apiKey={process.env.NEXT_PUBLIC_GOOGLE_PLACES_API_KEY}
                    selectProps={{
                      placesPostCode,
                      onChange: setPlacesPostcode,
                      styles: selectStyles,
                      placeholder: tx["Search"],
                      isClearable: true,
                    }}
                    styles={selectStyles}
                  />
                  <input
                    type="hidden"
                    id="signup-location"
                    value={JSON.stringify(placesPostCode)}
                  />
                  <FieldError fieldName="signup-location" allErrors={errors} />
                </FormGroup>
              </Col>

              <Col md={6}>
                <SignupField
                  id="club-email"
                  label={tx["Email"]}
                  mandatory={false}
                  type="email"
                />
                <FieldError fieldName="signup-club-email" allErrors={errors} />
              </Col>

              <Col md={6}>
                <FormGroup className="mb-3">
                  <label className="form-label" htmlFor="signup-phone-number">
                    {tx["Phone number"]}
                  </label>
                  <TelephoneInput id="signup-club-phone-number" />
                  <FieldError
                    fieldName="signup-club-phone-number"
                    allErrors={errors}
                  />
                </FormGroup>
              </Col>

              <Col md={6}>
                <SignupField
                  id="website"
                  label={tx["Website"]}
                  mandatory={false}
                  type="url"
                />
                <FieldError fieldName="signup-website" allErrors={errors} />
              </Col>

              <Col md={6}>
                <SignupField
                  id="club-type"
                  label={tx["Club type"]}
                  mandatory={true}
                  type="select"
                  options={[
                    { value: "public", label: tx["Public"] },
                    { value: "members-only", label: tx["Members-only"] },
                    { value: "pay-to-play", label: tx["Pay-to-play"] },
                    {
                      value: "members-and-pay-to-play",
                      label: tx["Members-and-pay-to-play"],
                    },
                  ]}
                />
                <FieldError fieldName="signup-club-type" allErrors={errors} />
              </Col>

              <Col xs={12} className="mb-3">
                <label className="form-label">{tx["Opening times"]}</label>
                <DayRows allErrors={errors} />
              </Col>

              <Col xs={12}>
                <div className="row mb-3">
                  <label className="form-label">{tx["Facilities"]}</label>

                  <Col md={6} className="mb-2">
                    <Form.Check
                      type="checkbox"
                      className="facilities-checkbox-container">
                      <Form.Check.Input
                        id="facilities-hospitality"
                        type="checkbox"
                        className="me-2"
                      />
                      <Form.Check.Label
                        className="fw-bold"
                        htmlFor="facilities-hospitality">
                        {tx["Hospitality"]}
                      </Form.Check.Label>
                    </Form.Check>
                  </Col>

                  <Col md={6} className="mb-2">
                    <Form.Check
                      type="checkbox"
                      className="facilities-checkbox-container">
                      <Form.Check.Input
                        id="facilities-equipment-rental"
                        type="checkbox"
                        className="me-2"
                      />
                      <Form.Check.Label
                        className="fw-bold"
                        htmlFor="facilities-equipment-rental">
                        {tx["Equipment rental"]}
                      </Form.Check.Label>
                    </Form.Check>
                  </Col>

                  <Col md={6} className="mb-2">
                    <Form.Check
                      type="checkbox"
                      className="facilities-checkbox-container">
                      <Form.Check.Input
                        id="facilities-changing-rooms"
                        type="checkbox"
                        className="me-2"
                      />
                      <Form.Check.Label
                        className="fw-bold"
                        htmlFor="facilities-changing-rooms">
                        {tx["Changing rooms"]}
                      </Form.Check.Label>
                    </Form.Check>
                  </Col>

                  <Col md={6} className="mb-2">
                    <Form.Check
                      type="checkbox"
                      className="facilities-checkbox-container">
                      <Form.Check.Input
                        id="facilities-parking-lot"
                        type="checkbox"
                        className="me-2"
                      />
                      <Form.Check.Label
                        className="fw-bold"
                        htmlFor="facilities-parking-lot">
                        {tx["Parking facilities"]}
                      </Form.Check.Label>
                    </Form.Check>
                  </Col>

                  <Col md={6} className="mb-2">
                    <Form.Check
                      type="checkbox"
                      className="facilities-checkbox-container">
                      <Form.Check.Input
                        id="facilities-pay-as-you-play"
                        type="checkbox"
                        className="me-2"
                      />
                      <Form.Check.Label
                        className="fw-bold"
                        htmlFor="facilities-pay-as-you-play">
                        {tx["Pay as you play"]}
                      </Form.Check.Label>
                    </Form.Check>
                  </Col>

                  <Col md={6} className="mb-2">
                    <Form.Check
                      type="checkbox"
                      className="facilities-checkbox-container">
                      <Form.Check.Input
                        id="facilities-showers"
                        type="checkbox"
                        className="me-2"
                      />
                      <Form.Check.Label
                        className="fw-bold"
                        htmlFor="facilities-showers">
                        {tx["Showers"]}
                      </Form.Check.Label>
                    </Form.Check>
                  </Col>

                  <Col xs={12}>
                    <textarea
                      id="facilities-others"
                      className="form-control mt-2"
                      placeholder={tx["Others..."]}
                      rows={3}
                    />
                  </Col>
                </div>
              </Col>

              <Col xs={12}>
                <FormGroup className="mb-3">
                  <label className="form-label" htmlFor="signup-about">
                    * {tx["Club description"]}
                  </label>
                  <textarea
                    id="signup-about"
                    className="form-control"
                    placeholder={tx["Tell us a bit more..."]}
                    rows={5}
                  />
                </FormGroup>
                <FieldError fieldName="signup-about" allErrors={errors} />
              </Col>
            </FormBlock>

            <CourtSection errors={errors} />
            <Col xs={12}>
              <FormBlock>
                <ReCaptcha
                  id="clubs-recaptcha"
                  className="d-flex justify-content-center"
                  onChange={setRecaptchaVerified}
                />
                <FieldError
                  className="text-center"
                  fieldName="clubs-recaptcha"
                  allErrors={errors}
                />
              </FormBlock>
            </Col>
          </Col>
        </Row>

        <Row>
          <Col className="text-center">
            <FieldError fieldName="generic-error" allErrors={errors} />
            <Button onClick={submit} isLoading={isLoading}>
              {submitting ? (
                <span className="text-white">
                  <span className="spinner-border spinner-border-sm" />
                </span>
              ) : (
                tx["clubs:Sign up"]
              )}
            </Button>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default FormSection;
